import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { getCustomerById, getOwnerProfileById, getSession, subscribeSession } from '../data/authStore';
import { getFavoriteCount } from '../data/favoritesStore';
import {
  getNotificationDeliveryPreferences,
  getNotificationsForCustomer,
  markAllNotificationsRead,
  processDueReminders,
} from '../data/engagementStore';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<
    Array<{ id: string; title: string; message: string; read: boolean; createdAt: string }>
  >([]);
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSyncExternalStore(subscribeSession, getSession, getSession);
  const owner = session?.role === 'owner' ? getOwnerProfileById(session.ownerId) : undefined;
  const customer = session?.role === 'customer' ? getCustomerById(session.userId) : undefined;
  const profileName = session?.role === 'admin' ? 'ADMİN' : session?.role === 'owner' ? owner?.fullName : customer?.fullName;
  const canAddField = session?.role === 'owner' && owner?.status === 'approved';
  const favCount = getFavoriteCount();
  const unreadCount = notifications.filter((item) => !item.read).length;
  const notifRef = useRef<HTMLDivElement | null>(null);
  const latestNotificationIdRef = useRef('');
  const initialLoadedRef = useRef(false);

  const notificationIdentity = useMemo(() => {
    if (!session) return { userId: undefined as string | undefined, phone: undefined as string | undefined };
    if (session.role === 'customer') {
      return { userId: session.userId, phone: customer?.phone };
    }
    if (session.role === 'owner') {
      return { userId: session.ownerId, phone: owner?.phone };
    }
    return { userId: undefined, phone: undefined };
  }, [session, customer?.phone, owner?.phone]);

  const playNotificationSound = () => {
    try {
      const audioCtx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
      gain.gain.setValueAtTime(0.06, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.4);
      oscillator.connect(gain);
      gain.connect(audioCtx.destination);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.4);
    } catch {
      // ignore
    }
  };

  const speakNotification = () => {
    try {
      if (!('speechSynthesis' in window)) return;
      const utter = new SpeechSynthesisUtterance('Yeni bildiriminiz var');
      utter.lang = 'tr-TR';
      utter.rate = 1;
      window.speechSynthesis.speak(utter);
    } catch {
      // ignore
    }
  };

  const pushBrowserNotification = (title: string, message: string) => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    try {
      new Notification(title, { body: message });
    } catch {
      // ignore
    }
  };

  const loadNotifications = () => {
    if (!notificationIdentity.userId && !notificationIdentity.phone) {
      setNotifications([]);
      return;
    }
    const list = getNotificationsForCustomer(notificationIdentity.userId, notificationIdentity.phone).slice(0, 15);
    setNotifications(list);
    if (list.length === 0) return;
    const latestId = list[0].id;
    if (!initialLoadedRef.current) {
      initialLoadedRef.current = true;
      latestNotificationIdRef.current = latestId;
      return;
    }
    if (latestNotificationIdRef.current !== latestId) {
      latestNotificationIdRef.current = latestId;
      const delivery = getNotificationDeliveryPreferences({
        userId: notificationIdentity.userId,
        contactPhone: notificationIdentity.phone,
      });
      if (delivery.browser) {
        pushBrowserNotification(list[0].title, list[0].message);
      }
      if (delivery.sound) {
        playNotificationSound();
        speakNotification();
      }
    }
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  const handleProfileClick = () => {
    setIsOpen(false);
    navigate('/profil');
  };

  useEffect(() => {
    processDueReminders();
    const timer = window.setInterval(() => {
      processDueReminders();
      loadNotifications();
    }, 10 * 1000);
    loadNotifications();
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationIdentity.userId, notificationIdentity.phone]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!notifRef.current) return;
      if (!notifRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center shadow-sm group-hover:bg-primary-700 transition-colors">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                  <path d="M12 2 L12 22 M2 12 L22 12" stroke="currentColor" strokeWidth="1.5"/>
                  <circle cx="12" cy="12" r="3" fill="currentColor"/>
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-800">
                Maç<span className="text-primary-600">Vakti</span>
              </span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/')
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Ana Sayfa
            </Link>
            <Link
              to="/sahalar"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/sahalar')
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Sahalar
            </Link>
            <Link
              to="/favoriler"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                isActive('/favoriler')
                  ? 'bg-red-50 text-red-600 font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill={isActive('/favoriler') ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                Favoriler
                {favCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                    {favCount}
                  </span>
                )}
              </span>
            </Link>
            {profileName ? (
              <button
                type="button"
                onClick={handleProfileClick}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200"
              >
                {profileName}
              </button>
            ) : (
              <Link
                to="/giris"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive('/giris')
                    ? 'bg-primary-50 text-primary-700 font-semibold'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Giriş Yap
              </Link>
            )}

            {profileName && (
              <div className="relative" ref={notifRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsNotifOpen((v) => !v);
                    if (unreadCount > 0) {
                      markAllNotificationsRead(notificationIdentity.userId, notificationIdentity.phone);
                      loadNotifications();
                    }
                  }}
                  className="relative px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200"
                  aria-label="Bildirimler"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0a3 3 0 11-6 0m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] leading-none px-1.5 py-1 rounded-full font-bold min-w-[18px] text-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {isNotifOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-900">Bildirimler</p>
                      {'Notification' in window && Notification.permission !== 'granted' && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await Notification.requestPermission();
                            } catch {
                              // ignore
                            }
                          }}
                          className="text-xs text-primary-700 border border-primary-200 bg-primary-50 rounded px-2 py-1"
                        >
                          Tarayıcı İzni Aç
                        </button>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="text-sm text-gray-500 px-3 py-4">Henüz bildiriminiz yok.</p>
                      ) : (
                        notifications.map((item) => (
                          <div key={item.id} className={`px-3 py-2 border-b border-gray-100 ${item.read ? 'bg-white' : 'bg-primary-50'}`}>
                            <p className="text-sm font-medium text-gray-900">{item.title}</p>
                            <p className="text-xs text-gray-600 mt-1">{item.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleProfileClick}
                      className="w-full text-sm text-primary-700 bg-primary-50 hover:bg-primary-100 py-2"
                    >
                      Tümünü Profilde Gör
                    </button>
                  </div>
                )}
              </div>
            )}

            {canAddField && (
              <>
                {/* Divider */}
                <div className="w-px h-6 bg-gray-200 mx-2" />

                {/* Saha Ekle Button */}
                <Link
                  to="/panel/saha-ekle"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 active:bg-primary-800 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Saha Ekle
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-lg transition-colors"
              aria-label="Menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-100">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/')
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Ana Sayfa
            </Link>
            <Link
              to="/sahalar"
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/sahalar')
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Sahalar
            </Link>
            <Link
              to="/favoriler"
              onClick={() => setIsOpen(false)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive('/favoriler')
                  ? 'bg-red-50 text-red-600 font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <svg className="w-4 h-4" fill={isActive('/favoriler') ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Favoriler {favCount > 0 && `(${favCount})`}
            </Link>
            {profileName ? (
              <button
                type="button"
                onClick={handleProfileClick}
                className="w-full text-left block px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors"
              >
                {profileName}
              </button>
            ) : (
              <Link
                to="/giris"
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/giris')
                    ? 'bg-primary-50 text-primary-700 font-semibold'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Giriş Yap
              </Link>
            )}

            {profileName && (
              <button
                type="button"
                onClick={() => {
                  markAllNotificationsRead(notificationIdentity.userId, notificationIdentity.phone);
                  setIsOpen(false);
                  navigate('/profil');
                }}
                className="w-full text-left block px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors"
              >
                Bildirimler {unreadCount > 0 ? `(${unreadCount})` : ''}
              </button>
            )}

            {canAddField && (
              <>
                {/* Mobile Divider */}
                <div className="border-t border-gray-100 my-2" />

                {/* Mobile Saha Ekle Button */}
                <Link
                  to="/panel/saha-ekle"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Saha Ekle
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
